import { api, ApiError } from "./client";
import type { ApiResponse } from "@/types/api";
import type { ApprovalChainDefinition } from "./approvalChains";

// External Intake & Collection v1.0 PR 2 — wire types for the
// System-Admin template builder. Mirrors the backend DTOs in
// PhoenixCapital.Core/DTOs/Collection/CollectionDtos.cs.

export type CollectionItemKind = "Form" | "Upload" | "ESign" | "Education";
export type SignaturePath = "InHouse" | "DocuSign";
export type CollectionPromptKind = "Feedback" | "Assessment";

// Tier 2 #2 — reuse the portal MaterialAudience enum on Collection
// templates. Re-exported here so collection callers don't need to know
// about the materials module.
export { MATERIAL_AUDIENCES, type MaterialAudience } from "./portalMaterials";
import { MATERIAL_AUDIENCES, type MaterialAudience } from "./portalMaterials";

// Onboarding differentiation — the collection template/bundle admin exposes two
// EXTRA audiences ("Brokerages", "Junior Brokers") so the owner can FILE the
// already-built brokerage / junior-broker onboarding content into them. These
// live only in this collection-scoped list, NOT in the shared
// MATERIAL_AUDIENCES array — that array also drives the Send-Signup-Link
// account-type picker and the portal-accounts filter, where a "JuniorBroker"
// audience is not a real portal account type and would provision a broken
// account. "Brokerage" already exists as a MaterialAudience (PR-5) but was kept
// out of the shared array on purpose; "JuniorBroker" is new to this change.
export const COLLECTION_AUDIENCES: MaterialAudience[] = [
  ...MATERIAL_AUDIENCES.filter((a) => a !== "All"),
  "Brokerage",
  "JuniorBroker",
  "All",
];

// Friendly labels for the two onboarding categories; every other audience falls
// back to its raw enum name (matching the pre-existing dropdown behaviour).
const COLLECTION_AUDIENCE_LABELS: Partial<Record<MaterialAudience, string>> = {
  Brokerage: "Brokerages",
  JuniorBroker: "Junior Brokers",
};

export function collectionAudienceLabel(a: MaterialAudience): string {
  return COLLECTION_AUDIENCE_LABELS[a] ?? a;
}

// Collection Templates — Audience Multiselect. A template targets a SET of
// audiences (sentinel encoding: ["All"] = everyone). The seven specific
// audiences in canonical order — All is the sentinel and is NOT in this list.
// Mirrors the backend CollectionAudienceSet.Specific (keep the two in lockstep).
export const SPECIFIC_COLLECTION_AUDIENCES: MaterialAudience[] = [
  "Broker",
  "Buyer",
  "Seller",
  "Vendor",
  "Tps",
  "Brokerage",
  "JuniorBroker",
];

/** Apply the sentinel encoding (spec D1 / §9): a selection that includes All —
 *  or names every specific audience — collapses to ["All"]; otherwise the
 *  distinct specifics in canonical order. An empty selection stays empty (the
 *  editor blocks save on empty, spec §3). */
export function normalizeAudiences(selection: MaterialAudience[]): MaterialAudience[] {
  const set = new Set(selection);
  if (set.has("All") || SPECIFIC_COLLECTION_AUDIENCES.every((a) => set.has(a))) {
    return ["All"];
  }
  return SPECIFIC_COLLECTION_AUDIENCES.filter((a) => set.has(a));
}

/** Render a template's audience set for display (spec §7): "All" for the
 *  sentinel, otherwise the labels comma-joined in canonical order. When `max`
 *  is given and there are more labels, truncate to the first `max` + a "+N"
 *  overflow; `title` always carries the full list for a hover tooltip. */
export function formatAudiences(
  audiences: MaterialAudience[],
  max?: number,
): { text: string; title: string } {
  const list = audiences.includes("All")
    ? (["All"] as MaterialAudience[])
    : SPECIFIC_COLLECTION_AUDIENCES.filter((a) => audiences.includes(a));
  const labels = (list.length > 0 ? list : (["All"] as MaterialAudience[])).map(
    collectionAudienceLabel,
  );
  const title = labels.join(", ");
  if (max != null && labels.length > max) {
    return { text: `${labels.slice(0, max).join(", ")} +${labels.length - max}`, title };
  }
  return { text: title, title };
}

export interface CollectionTemplateDto {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  version: number;
  kind: CollectionItemKind;
  // Collection Templates — Audience Multiselect. The SET of audiences this
  // template targets (sentinel encoding: ["All"] = everyone). Replaced the
  // former single `audience` string — the wire shape is now an array.
  audiences: MaterialAudience[];
  documentTagCode: string | null;
  instructions: string | null;
  formSchemaJson: string | null;
  eSignProvider: string | null;
  eSignTemplateRef: string | null;
  signerRoleName: string | null;
  requiresSignature: boolean;
  signaturePath: SignaturePath | null;
  feedbackPromptId: string | null;
  assessmentPromptId: string | null;
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
  educationContentBlobPath: string | null;
  educationContentType: string | null;
  educationContentSizeBytes: number | null;
  educationContentOriginalFileName: string | null;
  educationVideoUrl: string | null;
  educationMinimumDurationSeconds: number | null;
  createdAt: string;
  modifiedAt: string;
  /** Special forms — the generated-letter body ({{Token}} merge fields
   *  rendered with the sender-field values). Null for ordinary templates. */
  bodyTemplateMarkdown: string | null;
  /** Special forms — explicit designation; only meaningful for Kind=Form. */
  isSpecialForm: boolean;
  /** Approval chains (R-A) — the template's lineage (stable across versions).
   *  Optional for older fixtures; the server always sends it. */
  lineageId?: string;
  /** Approval chains (D3) — borrower-facing: Licensed Intermediary then Note
   *  Holder (both mandatory) ahead of any Borrower stage, plus the D9
   *  attestations on every send. */
  isBorrowerFacing?: boolean;
  /** Approval chains (D3) — the template's default chain; null = none. */
  approvalChain?: ApprovalChainDefinition | null;
  /** Upload templates (U6a) — this version's "document to download"; a null
   *  file name = none. The bytes come from `downloadFormAttachment` (never a
   *  URL). Optional for older fixtures; the server always sends them. */
  formAttachmentFileName?: string | null;
  formAttachmentContentType?: string | null;
  formAttachmentSizeBytes?: number | null;
  /** Null = the portal shows "Download form". */
  formAttachmentButtonLabel?: string | null;
  formAttachmentUploadedAt?: string | null;
  formAttachmentUploadedByName?: string | null;
}

/** Upload templates (U6a) — what `uploadFormAttachment` returns; saved onto a
 *  template version by sending it back as `formAttachment` on create/update. */
export interface FormAttachmentDescriptor {
  blobPath: string;
  contentType: string;
  sizeBytes: number;
  originalFileName: string;
}

export interface CreateCollectionTemplatePayload {
  name: string;
  description: string | null;
  kind: CollectionItemKind;
  // Collection Templates — Audience Multiselect. Null / empty defaults to
  // ["All"] server-side (the editor blocks empty on save, spec §3).
  audiences?: MaterialAudience[] | null;
  documentTagCode: string | null;
  instructions: string | null;
  formSchemaJson: string | null;
  eSignProvider: string | null;
  eSignTemplateRef: string | null;
  signerRoleName?: string | null;
  requiresSignature: boolean;
  signaturePath: SignaturePath | null;
  feedbackPromptId: string | null;
  assessmentPromptId: string | null;
  feedbackEnabled?: boolean | null;
  assessmentEnabled?: boolean | null;
  educationContentBlobPath?: string | null;
  educationContentType?: string | null;
  educationContentSizeBytes?: number | null;
  educationContentOriginalFileName?: string | null;
  educationVideoUrl?: string | null;
  educationMinimumDurationSeconds?: number | null;
  /** Special forms — SUPPLYING this key on update (even "") is an explicit
   *  body edit and requires the ADMIN-only Collection.ManageSpecialForms
   *  permission (403 otherwise). OMIT the key entirely (leave it undefined so
   *  JSON.stringify drops it) to carry the current body forward. */
  bodyTemplateMarkdown?: string | null;
  /** Special forms designation. Null/omitted = carry forward on update. NOT
   *  admin-gated (stays on Collection.ManageTemplates). */
  isSpecialForm?: boolean | null;
  /** Approval chains (D3) — SUPPLYING a chain requires the ADMIN-only
   *  Collection.ManageApprovalChains (403 otherwise). OMIT the key unless the
   *  user changed the chain; omitted = carry the current chain forward. */
  approvalChain?: ApprovalChainDefinition | null;
  /** Approval chains (D3) — admin-gated like the chain; omit unless changed. */
  isBorrowerFacing?: boolean | null;
  /** Upload templates (U6a) — a freshly uploaded document to attach (or to
   *  replace the current one). Omit to carry the current document forward. */
  formAttachment?: FormAttachmentDescriptor | null;
  /** Upload templates (U6a) — the download button's label. On update: omit =
   *  carry, "" = back to "Download form", text = set. */
  formAttachmentButtonLabel?: string | null;
}

export type UpdateCollectionTemplatePayload = Omit<CreateCollectionTemplatePayload, "kind"> & {
  /** Approval chains (D3) — true removes the chain (admin-gated; refused when
   *  the saved chain has mandatory stages). Omit unless the user removed it. */
  clearApprovalChain?: boolean | null;
  /** Upload templates (U6a) — true removes the document to download from the
   *  new version (requests already sent keep theirs). */
  clearFormAttachment?: boolean | null;
};

/** Upload templates (U6a) — the accepted attachment types (PDF, Word, Excel)
 *  and the portal upload size limit; the server re-checks both. */
export const FORM_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const FORM_ATTACHMENT_MAX_BYTES = 50_000_000;
export const FORM_ATTACHMENT_DEFAULT_BUTTON_LABEL = "Download form";

export interface EducationContentUploadResult {
  blobPath: string;
  contentType: string;
  sizeBytes: number;
  originalFileName: string;
}

export interface CollectionPromptDto {
  id: string;
  templateId: string;
  kind: CollectionPromptKind;
  body: string;
  version: number;
  createdAt: string;
  modifiedAt: string;
  modifiedByUserId: string | null;
}

export interface DraftCollectionPromptTurn {
  adminFeedback: string;
  assistantDraft: string;
}

export interface DraftCollectionPromptRequest {
  kind: CollectionPromptKind;
  description: string;
  refinementHistory: DraftCollectionPromptTurn[] | null;
}

export interface DraftCollectionPromptResult {
  draftedPromptBody: string;
  warnings: string[];
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  draftsUsedToday: number;
  dailyDraftLimit: number;
}

/**
 * Thrown when the backend returns 429 (shared per-user daily Prompt
 * Builder quota). Carries the parsed Retry-After so the modal can
 * surface "Resets in X seconds" without a follow-up call.
 */
export class CollectionPromptQuotaError extends Error {
  status = 429;
  retryAfterSeconds: number | null;
  errors: string[];
  constructor(message: string, errors: string[], retryAfterSeconds: number | null) {
    super(message);
    this.name = "CollectionPromptQuotaError";
    this.errors = errors;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export const collectionTemplatesApi = {
  list: (
    includeInactive = false,
    audience: MaterialAudience | null = null,
  ) =>
    api.get<CollectionTemplateDto[]>("/collection/templates", {
      includeInactive,
      audience: audience ?? undefined,
    }),
  get: (id: string) => api.get<CollectionTemplateDto>(`/collection/templates/${id}`),
  create: (body: CreateCollectionTemplatePayload) =>
    api.post<CollectionTemplateDto>("/collection/templates", body),
  update: (id: string, body: UpdateCollectionTemplatePayload) =>
    api.put<CollectionTemplateDto>(`/collection/templates/${id}`, body),
  deactivate: (id: string) =>
    api.post<null>(`/collection/templates/${id}/deactivate`),
  uploadEducationContent: (file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return api.upload<EducationContentUploadResult>(
      "/collection/templates/education-content", form);
  },
  /** Upload templates (U6a) — store a document to download; save the result
   *  onto the template through create/update. */
  uploadFormAttachment: (file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return api.upload<FormAttachmentDescriptor>("/collection/templates/form-attachment", form);
  },
  /** Upload templates (U6a) — the saved version's document, streamed. */
  downloadFormAttachment: (id: string) =>
    api.getBlob(`/collection/templates/${id}/form-attachment`),
};

export const collectionPromptsApi = {
  list: (templateId: string) =>
    api.get<CollectionPromptDto[]>(`/collection/templates/${templateId}/prompts`),
  get: (templateId: string, kind: CollectionPromptKind) =>
    api.get<CollectionPromptDto>(`/collection/templates/${templateId}/prompts/${kind}`),
  save: (templateId: string, kind: CollectionPromptKind, body: string) =>
    api.put<CollectionPromptDto>(
      `/collection/templates/${templateId}/prompts/${kind}`,
      { body },
    ),
};

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

/** Draft (or refine) a Collection-template prompt body via Claude. Hand-
 *  written fetch so the call can be aborted from the modal (the api.post
 *  helper doesn't surface the request signal). Parallels
 *  src/api/promptBuilder.ts. */
export async function draftCollectionPrompt(
  templateId: string,
  body: DraftCollectionPromptRequest,
  signal?: AbortSignal,
): Promise<DraftCollectionPromptResult> {
  const response = await fetch(
    `${API_BASE}/collection/templates/${templateId}/prompts/draft`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
      signal,
    },
  );

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson
    ? ((await response.json()) as ApiResponse<DraftCollectionPromptResult>)
    : null;

  if (response.status === 429) {
    const retryAfterRaw = response.headers.get("Retry-After");
    const retryAfter = retryAfterRaw ? Number.parseInt(retryAfterRaw, 10) : null;
    throw new CollectionPromptQuotaError(
      payload?.message ?? "Daily prompt drafting quota exceeded.",
      payload?.errors ?? [],
      Number.isFinite(retryAfter as number) ? (retryAfter as number) : null,
    );
  }

  if (!response.ok) {
    const errors = payload?.errors?.length ? payload.errors : [response.statusText];
    throw new ApiError(payload?.message ?? response.statusText, response.status, errors);
  }

  if (payload && "data" in payload && payload.data) {
    return payload.data;
  }
  throw new ApiError("Empty response from drafting service.", response.status, []);
}
